"use client";

import { useLanguage } from "@/app/context/LanguageContext";
import Image from "next/image";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Sparkles,
  Sunrise,
  Flame,
  MapPin,
  Phone,
  User,
  Heart,
  Clock,
  Star,
  ChevronDown,
} from "lucide-react";

const content = {
  en: {
    hero: {
      badge: "Divine Spiritual Destination",
      title: "18m Padi Karuppanasamy",
      subtitle: "The Divine Protector",
      cta: "Seek Blessings",
      scroll: "Discover More",
    },
    intro: {
      title: "Temple Overview",
      desc: "Inaugurated in 2014, the temple has successfully completed 12 years of profound devotion. It features a magnificent 22ft height Karuppanasamy statue, revered across generations.",
      deitiesTitle: "Deities Worshipped",
      deities: [
        "Karuppanasamy",
        "Kaali Amman",
        "Bairavar",
        "Murugan",
        "Vinayagar",
        "Navagram",
      ],
    },
    festivals: {
      title: "Upcoming Festivals",
      items: [
        { name: "Ammavaasai", tag: "Full Day Open" },
        { name: "Pavarnamni", tag: "Full Day Open" },
        { name: "Astami", tag: "Evening Special Pooja" },
        { name: "Navami", tag: "Evening Special Pooja" },
      ],
    },
    pooja: {
      title: "Darshan Timings",
      allDays: "Open All 7 Days",
      hours: [
        { label: "Morning Darshan", time: "4:00 AM – 12:00 PM" },
        { label: "Evening Darshan", time: "4:00 PM – 7:00 PM" },
      ],
      specialOpen: "Tuesday & Friday: Full Day Open",
      poojas: [
        { name: "Everyday Brama Murugatha", time: "4:30 AM – 6:00 AM" },
        { name: "Astami & Navami Special", time: "4:30 PM – 6:00 PM" },
      ],
    },
    galleryTitle: "Sacred Gallery",
    donation: {
      title: "Seva & Donation",
      desc: "Support the temple's maintenance and sacred rituals. Your devotion strengthens our heritage.",
      cta: "Make a Donation",
    },
    contact: {
      title: "Contact & Location",
      address: "Kadavoor Thaluk, Kaniyalampatti Road, Sukkampatti, Karur",
      phones: ["9597255401", "9361986095"],
      trusteeTitle: "Trustee Details",
      trusteeNames: [
        "Kalimuthu (Parents: Vengatajalam - Palaniyamaal)",
        "Brother: Krishnamoorthy",
      ],
      labor: "Maintained by 2 Male dedicated staff",
    },
  },
  ta: {
    hero: {
      badge: "புனித ஆன்மீக தலம்",
      title: "18ம் படி கருப்பண்ணசாமி",
      subtitle: "காவல் தெய்வம்",
      cta: "ஆசி பெறுக",
      scroll: "மேலும் அறிக",
    },
    intro: {
      title: "கோவில் வரலாறு",
      desc: "2014 ஆம் ஆண்டு திறக்கப்பட்ட இக்கோவில் 12 ஆண்டுகளை வெற்றிகரமாக நிறைவு செய்துள்ளது. 22 அடி உயரமுள்ள கருப்பண்ணசாமி சிலையுடன் பக்தர்களுக்கு அருள்பாலிக்கிறது.",
      deitiesTitle: "தெய்வங்கள்",
      deities: [
        "கருப்பண்ணசாமி",
        "காளி அம்மன்",
        "பைரவர்",
        "முருகன்",
        "விநாயகர்",
        "நவகிரகம்",
      ],
    },
    festivals: {
      title: "வரவிருக்கும் சிறப்பு நாட்கள்",
      items: [
        { name: "அமாவாசை", tag: "முழு நேர தரிசனம்" },
        { name: "பௌர்ணமி", tag: "முழு நேர தரிசனம்" },
        { name: "அஷ்டமி", tag: "மாலை சிறப்பு பூஜை" },
        { name: "நவமி", tag: "மாலை சிறப்பு பூஜை" },
      ],
    },
    pooja: {
      title: "விரைவு தரிசன நேரங்கள்",
      allDays: "வாரம் 7 நாட்களும் திறந்திருக்கும்",
      hours: [
        { label: "காலை தரிசனம்", time: "4:00 AM - 12:00 PM" },
        { label: "மாலை தரிசனம்", time: "4:00 PM - 7:00 PM" },
      ],
      specialOpen: "செவ்வாய் & வெள்ளி: முழு நேர தரிசனம்",
      poojas: [
        {
          name: "தினசரி பிரம்ம முகூர்த்த பூஜை",
          time: "4:30 AM முதல் 6:00 AM வரை",
        },
        {
          name: "அஷ்டமி & நவமி சிறப்பு பூஜை",
          time: "4:30 PM முதல் 6:00 PM வரை",
        },
      ],
    },
    galleryTitle: "புகைப்பட தொகுப்பு",
    donation: {
      title: "கோவிலுக்கு காணிக்கை",
      desc: "கோவில் வளர்ச்சிக்கும் பூஜைகளுக்கும் உங்கள் நன்கொடைகள் பெரிதும் உதவும்.",
      cta: "நன்கொடை அளியுங்கள்",
    },
    contact: {
      title: "தொடர்பு மற்றும் முகவரி",
      address: "கடவூர் தாலுகா, கணியாலம்பட்டி ரோடு, சுக்கம்பட்டி, கரூர்",
      phones: ["9597255401", "9361986095"],
      trusteeTitle: "நிர்வாகிகள்",
      trusteeNames: [
        "களிமுத்து (பெற்றோர்: வெங்கடாசலம் - பழனியம்மாள்)",
        "சகோதரர்: கிருஷ்ணமூர்த்தி",
      ],
      labor: "2 அன்பர்கள் கோவிலில் சேவை செய்கின்றனர்",
    },
  },
};

const placeholderImages = [
  "/home/img1(1).jpg",
  "/home/img2.jpg",
  "/home/img3.jpg",
  "/home/img4.jpg",
];

const festivalIcons = [Star, Sparkles, Flame, Sunrise];

export default function HomePage() {
  const { language } = useLanguage();
  const t = content[language as "en" | "ta"] ?? content.en;

  return (
    <main className="bg-stone-950 text-stone-100 min-h-screen w-full max-w-[100vw] overflow-x-hidden selection:bg-amber-800 selection:text-amber-100">
      {/* ── HERO ── */}
      <section className="relative min-h-screen w-full flex flex-col justify-center items-center text-center px-4 overflow-hidden">
        {/* Background Banner Image */}
        <div className="absolute inset-0 z-0 w-full h-full">
          <Image
            src={placeholderImages[0]}
            alt="Hero Banner Background"
            fill
            priority
            sizes="100vw"
            className="object-cover object-center sm:object-top opacity-40 transition-all duration-700 w-full h-full"
          />
          {/* Dark overlay for responsive text readability */}
          <div className="absolute inset-0 bg-stone-950/70 sm:bg-stone-950/60 bg-gradient-to-b from-stone-950/50 via-transparent to-stone-950 w-full h-full" />
        </div>

        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(180,83,9,0.18),transparent_70%)] pointer-events-none w-full h-full" />
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(120,53,15,0.12),transparent_70%)] pointer-events-none w-full h-full" />

        {/* Decorative lines top-right */}
        <div className="absolute top-0 right-0 w-80 h-80 opacity-10 overflow-hidden pointer-events-none">
          <div className="absolute top-8 right-0 w-px h-64 bg-gradient-to-b from-transparent via-amber-400 to-transparent rotate-[20deg] origin-top" />
          <div className="absolute top-0 right-16 w-px h-48 bg-gradient-to-b from-transparent via-amber-300 to-transparent rotate-[20deg] origin-top" />
          <div className="absolute top-4 right-32 w-px h-56 bg-gradient-to-b from-transparent via-amber-500 to-transparent rotate-[20deg] origin-top" />
        </div>
        <div className="absolute bottom-0 left-0 w-80 h-80 opacity-10 overflow-hidden pointer-events-none">
          <div className="absolute bottom-8 left-0 w-px h-64 bg-gradient-to-t from-transparent via-amber-400 to-transparent -rotate-[20deg] origin-bottom" />
          <div className="absolute bottom-0 left-16 w-px h-48 bg-gradient-to-t from-transparent via-amber-300 to-transparent -rotate-[20deg] origin-bottom" />
        </div>

        {/* Om watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <span className="text-[28rem] font-serif text-amber-900/[0.04] leading-none">
            ॐ
          </span>
        </div>

        <div className="relative z-10 w-full max-w-5xl mx-auto mt-24 flex flex-col items-center gap-6">
          <Badge className="bg-amber-950/60 text-amber-300 border border-amber-700/50 backdrop-blur-sm px-5 py-1.5 text-[0.65rem] tracking-[0.25em] uppercase font-semibold rounded-full">
            ✦ {t.hero.badge} ✦
          </Badge>

          <h1 className="w-full text-3xl sm:text-6xl md:text-8xl font-black tracking-tight leading-[1.1] sm:leading-[0.9] font-serif break-words max-w-full px-2">
            <span className="bg-gradient-to-br from-yellow-200 via-amber-400 to-orange-500 bg-clip-text text-transparent drop-shadow-[0_0_60px_rgba(251,191,36,0.3)]">
              {t.hero.title}
            </span>
          </h1>

          <div className="flex items-center gap-3 text-amber-600/60">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-600/60" />
            <span className="text-lg">✦</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-600/60" />
          </div>

          <p className="text-lg sm:text-2xl md:text-3xl text-amber-200/70 font-light tracking-[0.15em] sm:tracking-[0.2em] uppercase px-4">
            {t.hero.subtitle}
          </p>

          <div className="flex flex-wrap justify-center gap-6 sm:gap-16 mt-4 w-full px-4">
            {[
              { val: "2014", lbl: "Established" },
              { val: "12+", lbl: "Years" },
              { val: "22ft", lbl: "Statue" },
            ].map((s) => (
              <div key={s.lbl} className="flex flex-col items-center gap-0.5 min-w-[80px]">
                <span className="text-2xl sm:text-4xl font-bold text-amber-400 font-serif">
                  {s.val}
                </span>
                <span className="text-[0.55rem] sm:text-[0.6rem] text-amber-600/80 tracking-[0.2em] uppercase">
                  {s.lbl}
                </span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            {/* CONTACT BUTTON */}
            <Link href="/contact">
              <Button
                size="lg"
                className="rounded-full bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 hover:from-amber-500 hover:via-orange-400 hover:to-amber-500 text-white border-none shadow-[0_0_40px_rgba(217,119,6,0.4)] px-10 py-6 text-base font-bold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-[0_0_60px_rgba(217,119,6,0.6)]"
              >
                {t.hero.cta}
              </Button>
            </Link>

            {/* ABOUT BUTTON */}
            <Link href="/about">
              <Button
                variant="ghost"
                size="lg"
                className="rounded-full text-amber-400/70 hover:text-amber-300 hover:bg-amber-950/40 border border-amber-800/40 px-8 py-6 text-base tracking-wide transition-all duration-300"
              >
                {t.hero.scroll}
              </Button>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-stone-950 to-transparent" />
      </section>

      {/* ── INTRO ── */}
      <section className="py-28 relative">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-48 bg-gradient-to-b from-transparent via-amber-600 to-transparent" />
        <div className="container mx-auto px-6 lg:px-16 max-w-6xl">
          <div className="flex flex-col items-start mb-16 gap-3">
            <span className="text-[0.65rem] text-amber-500/70 tracking-[0.35em] uppercase font-semibold">
              — Sacred Space
            </span>
            <h2 className="text-4xl md:text-6xl font-serif font-black text-stone-100">
              {t.intro.title}
            </h2>
            <div className="h-0.5 w-24 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full" />
          </div>

          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3 space-y-8">
              <Card className="bg-gradient-to-br from-stone-900 via-stone-900 to-stone-950 border border-stone-800/80 shadow-2xl overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-amber-900/20 to-transparent" />
                <div className="absolute top-3 right-3 text-amber-600/30 text-4xl font-serif select-none">
                  ✦
                </div>
                <CardContent className="p-6 sm:p-8 md:p-10 relative z-10">
                  <p className="text-base sm:text-lg leading-7 sm:leading-8 text-stone-300 font-light">
                    {t.intro.desc}
                  </p>
                  <Separator className="my-6 sm:my-8 bg-stone-800" />
                  <div className="grid grid-cols-2 xs:grid-cols-3 gap-3 sm:gap-4">
                    {[
                      { val: "2014", lbl: "Established" },
                      { val: "12", lbl: "Years Completed" },
                      { val: "22ft", lbl: "Statue Height" },
                    ].map((s, i) => (
                      <div
                        key={i}
                        className={`text-center py-4 rounded-xl bg-stone-950/60 border border-stone-800 ${i === 2 ? "col-span-2 xs:col-span-1" : ""}`}
                      >
                        <div className="text-2xl sm:text-3xl font-bold text-amber-400 font-serif">
                          {s.val}
                        </div>
                        <div className="text-[0.55rem] sm:text-[0.6rem] text-stone-500 uppercase tracking-widest mt-1">
                          {s.lbl}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2 space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-stone-800" />
                <span className="text-xs text-amber-500 tracking-widest uppercase font-semibold">
                  {t.intro.deitiesTitle}
                </span>
                <div className="h-px flex-1 bg-stone-800" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {t.intro.deities.map((deity, idx) => (
                  <div
                    key={idx}
                    className="group relative px-4 py-3.5 rounded-xl bg-stone-900 border border-stone-800 hover:border-amber-700/60 hover:bg-stone-800/80 transition-all duration-300 cursor-default overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-900/0 to-amber-900/0 group-hover:from-amber-900/10 group-hover:to-transparent transition-all duration-300" />
                    <div className="flex items-center gap-2.5 relative z-10">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 group-hover:bg-amber-400 transition-colors flex-shrink-0" />
                      <span className="text-sm text-stone-300 group-hover:text-amber-200 transition-colors font-medium leading-tight">
                        {deity}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FESTIVALS ── */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_50%,rgba(120,53,15,0.08),transparent_70%)]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-800/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-800/40 to-transparent" />

        <div className="container mx-auto px-6 lg:px-16 max-w-6xl relative z-10">
          <div className="text-center mb-16 space-y-3">
            <span className="text-[0.65rem] text-amber-500/70 tracking-[0.35em] uppercase font-semibold">
              — Sacred Calendar
            </span>
            <h2 className="text-4xl md:text-6xl font-serif font-black text-stone-100">
              {t.festivals.title}
            </h2>
            <div className="h-0.5 w-24 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full mx-auto" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.festivals.items.map((item, idx) => {
              const Icon = festivalIcons[idx % festivalIcons.length];
              return (
                <Card
                  key={idx}
                  className="group bg-stone-900 hover:bg-stone-800/90 border border-stone-800 hover:border-amber-700/50 transition-all duration-500 overflow-hidden relative cursor-default"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-900/0 group-hover:from-amber-900/10 group-hover:to-transparent transition-all duration-500" />
                  <div className="absolute top-3 right-4 text-6xl font-black text-stone-800 group-hover:text-amber-900/30 transition-colors duration-500 select-none font-serif leading-none">
                    {idx + 1}
                  </div>
                  <CardContent className="p-7 relative z-10 space-y-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-950/50 border border-amber-900/50 group-hover:border-amber-700/60 flex items-center justify-center text-amber-500 group-hover:text-amber-400 transition-all duration-300">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-stone-100 font-serif mb-1.5">
                        {item.name}
                      </h4>
                      <span className="text-xs text-amber-500/80 tracking-wider uppercase font-medium">
                        {item.tag}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TIMINGS ── */}
      <section className="py-28 relative">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-48 bg-gradient-to-b from-transparent via-amber-600 to-transparent" />
        <div className="container mx-auto px-6 lg:px-16 max-w-6xl">
          <div className="flex flex-col items-end mb-16 gap-3 text-right">
            <span className="text-[0.65rem] text-amber-500/70 tracking-[0.35em] uppercase font-semibold">
              — Divine Schedule
            </span>
            <h2 className="text-4xl md:text-6xl font-serif font-black text-stone-100">
              {t.pooja.title}
            </h2>
            <div className="h-0.5 w-24 bg-gradient-to-l from-amber-500 to-orange-600 rounded-full" />
          </div>

          <div className="mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 sm:px-6 py-4 rounded-2xl bg-amber-950/30 border border-amber-800/30">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <span className="text-xs sm:text-sm text-amber-200/80 font-medium">
                {t.pooja.allDays}
              </span>
            </div>
            <Separator
              orientation="vertical"
              className="hidden sm:block h-6 bg-amber-800/40"
            />
            <span className="text-xs sm:text-sm text-amber-300/70 text-center">
              {t.pooja.specialOpen}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-stone-900 border-stone-800 overflow-hidden relative">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600" />
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-950/50 border border-amber-900/50 flex items-center justify-center">
                    <Sunrise className="h-5 w-5 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-100 tracking-wide">
                    Darshan Timings
                  </h3>
                </div>
                <div className="space-y-3">
                  {t.pooja.hours.map((h, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-5 py-3.5 rounded-xl bg-stone-950/60 border border-stone-800"
                    >
                      <span className="text-sm text-stone-400 font-medium">
                        {h.label}
                      </span>
                      <span className="text-sm text-amber-400 font-bold tracking-wide">
                        {h.time}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-stone-900 border-stone-800 overflow-hidden relative">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600" />
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-950/50 border border-amber-900/50 flex items-center justify-center">
                    <Flame className="h-5 w-5 text-orange-500" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-100 tracking-wide">
                    Pooja Details
                  </h3>
                </div>
                <div className="space-y-3">
                  {t.pooja.poojas.map((p, i) => (
                    <div
                      key={i}
                      className="px-5 py-4 rounded-xl bg-stone-950/60 border border-stone-800 space-y-2"
                    >
                      <p className="text-sm text-stone-300 font-semibold leading-snug">
                        {p.name}
                      </p>
                      <span className="inline-block text-xs text-amber-400 bg-amber-950/60 border border-amber-900/50 px-3 py-1 rounded-full font-semibold tracking-wide">
                        {p.time}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      {/* <section className="py-28 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-800/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-16 max-w-6xl">
          <div className="text-center mb-16 space-y-3">
            <span className="text-[0.65rem] text-amber-500/70 tracking-[0.35em] uppercase font-semibold">— Darshan</span>
            <h2 className="text-4xl md:text-6xl font-serif font-black text-stone-100">{t.galleryTitle}</h2>
            <div className="h-0.5 w-24 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full mx-auto" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2 lg:col-span-2 relative aspect-[16/9] rounded-2xl overflow-hidden group ring-1 ring-stone-800">
              <Image src={placeholderImages[0]} alt="Temple gallery" fill className="object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5">
                <span className="text-xs text-amber-300/80 uppercase tracking-widest font-semibold">Featured</span>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {placeholderImages.slice(1).map((src, idx) => (
                <div key={idx} className="relative aspect-[4/3] rounded-2xl overflow-hidden group ring-1 ring-stone-800">
                  <Image src={src} alt="Gallery preview" fill className="object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-stone-950/20 group-hover:bg-transparent transition-colors duration-500" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      <section className="py-28 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-800/40 to-transparent" />

        <div className="container mx-auto px-6 lg:px-16 max-w-7xl">
          {/* Heading */}
          <div className="text-center mb-16 space-y-3">
            <span className="text-[0.65rem] text-amber-500/70 tracking-[0.35em] uppercase font-semibold">
              — Darshan
            </span>

            <h2 className="text-4xl md:text-6xl font-serif font-black text-stone-100">
              {t.galleryTitle}
            </h2>

            <div className="h-0.5 w-24 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full mx-auto" />
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {placeholderImages.slice(0, 4).map((src, idx) => (
              <div
                key={idx}
                className="relative aspect-[16/10] rounded-3xl overflow-hidden group ring-1 ring-stone-800 bg-stone-900"
              >
                <Image
                  src={src}
                  alt={`Temple Gallery ${idx + 1}`}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                {/* Optional Label */}
                <div className="absolute bottom-4 left-4">
                  {/* <span className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">
                    Darshan
                  </span> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── CONTACT + DONATION ── */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-800/40 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_100%,rgba(120,53,15,0.1),transparent_70%)]" />

        <div className="container mx-auto px-6 lg:px-16 max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-10">
              <div className="space-y-3">
                <span className="text-[0.65rem] text-amber-500/70 tracking-[0.35em] uppercase font-semibold">
                  — Find Us
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-black text-stone-100">
                  {t.contact.title}
                </h2>
                <div className="h-0.5 w-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full" />
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: MapPin,
                    title: "Address",
                    content: t.contact.address,
                  },
                  {
                    icon: Phone,
                    title: "Phone",
                    content: t.contact.phones.join("  /  "),
                  },
                ].map(({ icon: Icon, title, content }) => (
                  <div
                    key={title}
                    className="flex gap-4 p-5 rounded-2xl bg-stone-900 border border-stone-800 hover:border-amber-800/50 transition-colors duration-300 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-amber-950/50 border border-amber-900/50 group-hover:border-amber-700/60 flex shrink-0 items-center justify-center text-amber-500 transition-all duration-300">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs text-stone-500 uppercase tracking-widest font-semibold">
                        {title}
                      </p>
                      <p className="text-stone-300 text-sm leading-relaxed">
                        {content}
                      </p>
                    </div>
                  </div>
                ))}

                <div className="flex gap-4 p-5 rounded-2xl bg-stone-900 border border-stone-800 hover:border-amber-800/50 transition-colors duration-300 group">
                  <div className="w-11 h-11 rounded-xl bg-amber-950/50 border border-amber-900/50 group-hover:border-amber-700/60 flex shrink-0 items-center justify-center text-amber-500 transition-all duration-300">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-stone-500 uppercase tracking-widest font-semibold">
                      {t.contact.trusteeTitle}
                    </p>
                    {t.contact.trusteeNames.map((name, i) => (
                      <p key={i} className="text-stone-300 text-sm">
                        {name}
                      </p>
                    ))}
                    <p className="text-stone-600 text-xs italic pt-1">
                      {t.contact.labor}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Donation */}
            <div className="relative">
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-amber-600/30 via-orange-700/20 to-amber-900/10 blur-sm" />
              <Card className="relative bg-gradient-to-br from-stone-900 via-stone-900 to-amber-950/20 border border-amber-900/40 rounded-3xl overflow-hidden">
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-amber-600/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-orange-800/10 rounded-full blur-3xl pointer-events-none" />
                <CardContent className="p-6 md:p-12 flex flex-col items-center text-center gap-7 relative z-10">
                  <div className="relative">
                    <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-xl" />
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-amber-900/60 to-orange-900/40 border border-amber-700/40 flex items-center justify-center">
                      <Heart className="h-7 w-7 sm:h-9 sm:w-9 text-amber-400 fill-amber-500/40" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-2xl sm:text-4xl font-serif font-black text-amber-300">
                      {t.donation.title}
                    </h3>
                    <p className="text-stone-400 leading-relaxed text-sm sm:text-base max-w-xs mx-auto">
                      {t.donation.desc}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 w-full max-w-xs text-amber-700/40">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-700/40" />
                    <span>✦</span>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-700/40" />
                  </div>
                  <a
                    href={`tel:${t.contact.phones[0]}`}
                    className="w-full max-w-xs"
                  >
                    <Button
                      size="lg"
                      className="w-full rounded-full bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 hover:from-amber-500 hover:via-orange-400 hover:to-amber-500 text-white shadow-[0_0_40px_rgba(217,119,6,0.35)] hover:shadow-[0_0_60px_rgba(217,119,6,0.55)] transition-all duration-300 hover:scale-105 py-6 text-base font-bold tracking-wide border-none"
                    >
                      {t.donation.cta}
                    </Button>
                  </a>
                  <p className="text-xs text-amber-600/50 tracking-widest">
                    {t.contact.phones[0]}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 border-t border-stone-900">
        <div className="container mx-auto px-6 max-w-6xl flex flex-col sm:flex-row items-center justify-between gap-4 text-center">
          <span className="text-stone-600 text-xs tracking-widest uppercase">
            18m Padi Karuppanasamy Temple
          </span>
          <span className="text-amber-700/50 text-lg font-serif">✦ ॐ ✦</span>
          <span className="text-stone-700 text-xs">Karur, Tamil Nadu</span>
        </div>
      </footer>
    </main>
  );
}
